import cloneDeep from 'lodash/cloneDeep';
import type { MutationUpdaterFn } from 'apollo-client';
import memoizeOne from 'memoize-one';

import { readQueryInterop } from '@confluence/apollo-relay-interop/entry-points/readQuery.ts';
import { writeQueryInterop } from '@confluence/apollo-relay-interop/entry-points/writeQuery.ts';
import {
	BLOG_CONTENT_BLUEPRINT_ID,
	updateFavouriteTemplates,
} from '@confluence/template-utils/entry-points/templateUtils.ts';

import TemplateListQuery from './__generated__/TemplateListQuery.graphql.ts';
import type {
	TemplateListQuery as TemplateListQueryType,
	TemplateListQueryVariables as TemplateListQueryVariablesType,
	TemplateListQuery_allTemplates_PaginatedTemplateInfoList_nodes_TemplateInfo as Template,
} from './__types__/TemplateListQuery.ts';

export const updateFavouriteTemplatesAllTemplatesQuery: (
	spaceKey: string,
	sortingScheme: string,
	isLicensed: boolean,
	teamType?: string | null,
) => MutationUpdaterFn =
	(spaceKey, sortingScheme, isLicensed, teamType) =>
	(_cache, { data }) => {
		if (!data) {
			return;
		}

		const cachedResponse = cloneDeep(
			readQueryInterop<TemplateListQueryType, TemplateListQueryVariablesType>({
				query: TemplateListQuery,
				variables: { spaceKey, sortingScheme, isLicensed, teamType },
			}),
		);

		const templateNodes = cachedResponse?.allTemplates?.nodes || [];
		const updatedFavouriteTemplatesList =
			data?.updateUserPreferences?.favouriteTemplateEntityIds || [];
		const updatedTemplateNodes = updateFavouriteTemplates(
			templateNodes,
			updatedFavouriteTemplatesList,
		);

		// @ts-ignore
		cachedResponse.userPreferences = data?.updateUserPreferences;
		// @ts-ignore
		cachedResponse.allTemplates.nodes = updatedTemplateNodes;

		writeQueryInterop({
			query: TemplateListQuery,
			data: cachedResponse,
			variables: { spaceKey, sortingScheme, isLicensed, teamType },
		});
	};

// Blog post blueprint helpers
const templateIsBlogPost = (template) => template.contentBlueprintId === BLOG_CONTENT_BLUEPRINT_ID;

const removeBlogItem = memoizeOne((templates): Template[] =>
	templates.filter((template) => !templateIsBlogPost(template)),
);

const addBlogItem = memoizeOne((templates, blogTemplate, locale): Template[] => {
	// Find index for blogTemplate, or the index of the first template which doesn't have a recommendationRank
	// and is alphabetically after blog item
	const index = templates.findIndex(
		(template) =>
			!template.recommendationRank && template.name.localeCompare(blogTemplate.name, locale) > 0,
	);
	// Remove blogTemplate's recommendationRank, since it is not recommended in the selected space
	delete blogTemplate.recommendationRank;
	// Insert blog item at index
	return index < 0
		? [...templates, blogTemplate]
		: [...templates.slice(0, index), blogTemplate, ...templates.slice(index)];
});

/**
 * This function returns a list of the page templates from selectedSpaceTemplates and the non-page content type
 * templates from currentSpaceTemplates. The user has permission to use all returned templates in the current space.
 *
 * Right now, Blog is the only non-page content type which has space-level create permissions.
 *
 * @param selectedSpaceTemplates, list of all templates available to the user in the selected space
 * @param currentSpaceTemplates, list of all templates available to the user in the current space
 */
export const mergeCurrentAndSelectedTemplates = (
	selectedSpaceTemplates,
	currentSpaceTemplates,
	locale,
) => {
	const currentSpaceBlogItem = currentSpaceTemplates.find(templateIsBlogPost);
	const selectedSpaceBlogItem = selectedSpaceTemplates.find(templateIsBlogPost);

	let mergedTemplates = selectedSpaceTemplates;
	if (!currentSpaceBlogItem && !!selectedSpaceBlogItem) {
		mergedTemplates = removeBlogItem(selectedSpaceTemplates);
	} else if (!!currentSpaceBlogItem && !selectedSpaceBlogItem) {
		mergedTemplates = addBlogItem(selectedSpaceTemplates, currentSpaceBlogItem, locale);
	}

	return mergedTemplates;
};
